import React, { useState, useEffect } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import usuarioService from '../services/usuarioService';
import servicioService from '../services/servicioService';

const UsuariosEmpresas = () => {
  const [conteos, setConteos] = useState({ pending: 0, pendingAdmin: 0, active: 0 });
  const [loadingConteos, setLoadingConteos] = useState(true);
  const [conteosCategorias, setConteosCategorias] = useState({});
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [conteosRoles, setConteosRoles] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(true);
  
  // Estados para la tabla de empresas
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('lead'); // 'lead', 'servicios', 'pagado'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' o 'desc'
  const itemsPerPage = 20;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  useEffect(() => {
    const fetchConteos = async () => {
      try {
        setLoadingConteos(true);
        const [pendingResponse, pendingAdminResponse, activeResponse] = await Promise.all([
          usuarioService.getCompanies(1, 1, { estado: 'pending' }),
          usuarioService.getCompanies(1, 1, { estado: 'pendingAdmin' }),
          usuarioService.getCompanies(1, 1, { estado: 'active' })
        ]);
        setConteos({
          pending: pendingResponse.totalCount || pendingResponse.total || 0,
          pendingAdmin: pendingAdminResponse.totalCount || pendingAdminResponse.total || 0,
          active: activeResponse.totalCount || activeResponse.total || 0
        });
      } catch (error) {
        console.error('Error al obtener conteos de empresas:', error);
      } finally {
        setLoadingConteos(false);
      }
    };
    fetchConteos();
  }, []);
  
  useEffect(() => {
    const fetchConteosCategorias = async () => {
      try {
        setLoadingCategorias(true);
        const response = await usuarioService.getCompaniesCountByCategory('active');
        const conteosObj = {};
        if (Array.isArray(response)) {
          response.forEach(item => {
            conteosObj[item.category || item.categoria] = item.count || item.total || 0;
          });
        }
        setConteosCategorias(conteosObj);
      } catch (error) {
        console.error('Error al obtener conteos por categoría:', error);
      } finally {
        setLoadingCategorias(false);
      }
    };
    fetchConteosCategorias();
  }, []);
  
  useEffect(() => {
    const fetchConteosRoles = async () => {
      try {
        setLoadingRoles(true);
        const response = await usuarioService.getUsersCountByRole();
        const conteosObj = {};
        let totalUsers = 0;
        if (Array.isArray(response)) {
          response.forEach(item => {
            const role = item.role || item.rol;
            const count = item.count || item.total || 0;
            conteosObj[role] = count;
            totalUsers += count;
          });
        }
        conteosObj['total'] = totalUsers;
        setConteosRoles(conteosObj);
      } catch (error) {
        console.error('Error al obtener conteos por rol:', error);
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchConteosRoles();
  }, []);

  // Obtener datos de empresas del mes actual
  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        setLoadingEmpresas(true);
        const data = await servicioService.getMonthlyCompanyStats(currentYear, currentMonth);
        
        if (Array.isArray(data)) {
          setEmpresas(data);
        } else {
          setEmpresas([]);
        }
      } catch (error) {
        console.error('Error al obtener empresas:', error);
        setEmpresas([]);
      } finally {
        setLoadingEmpresas(false);
      }
    };
    fetchEmpresas();
  }, [currentYear, currentMonth]);

  // Función para ordenar empresas
  const getSortedEmpresas = () => {
    const sorted = [...empresas].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'servicios':
          valueA = a.serviceCount || 0;
          valueB = b.serviceCount || 0;
          break;
        case 'pagado':
          valueA = a.amountDue || 0;
          valueB = b.amountDue || 0;
          break;
        case 'lead':
        default:
          valueA = a.lead || 0;
          valueB = b.lead || 0;
          break;
      }
      
      return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
    });
    
    return sorted;
  };

  // Obtener empresas de la página actual
  const getPaginatedEmpresas = () => {
    const sorted = getSortedEmpresas();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  };

  // Calcular número total de páginas
  const totalPages = Math.ceil(empresas.length / itemsPerPage);

  // Función para cambiar ordenamiento
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Volver a la primera página al ordenar
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Usuarios y Empresas</h2>
      </div>
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6">Empresas Xervix</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Pendientes suscripción</h3>
              <span className="text-blue-500 bg-blue-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-800">
              {loadingConteos ? <span className="text-gray-400">...</span> : conteos.pending}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Pendientes aprobación</h3>
              <span className="text-purple-500 bg-purple-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-800">
              {loadingConteos ? <span className="text-gray-400">...</span> : conteos.pendingAdmin}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Empresas Activas</h3>
              <span className="text-yellow-500 bg-yellow-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-800">
              {loadingConteos ? <span className="text-gray-400">...</span> : conteos.active}
            </p>
          </div>
        </div>
        
        {/* 10 Cards de categorías */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-3">
          {[
            { name: 'Plomería', key: ['Plomeria', 'Plomería'], color: 'blue', icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
            { name: 'Electricidad', key: ['Electricidad'], color: 'yellow', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
            { name: 'Pintura', key: ['Pintura'], color: 'pink', icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01' },
            { name: 'Limpieza', key: ['Limpieza'], color: 'cyan', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
            { name: 'Mantenimiento', key: ['Mantenimiento'], color: 'orange', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
            { name: 'Obra Blanca', key: ['Obra Blanca', 'Obra blanca'], color: 'gray', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
            { name: 'Carpintería', key: ['Carpinteria', 'Carpintería'], color: 'amber', icon: 'M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z' },
            { name: 'Jardinería', key: ['Jardineria', 'Jardinería'], color: 'green', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
            { name: 'Confección', key: ['Confeccion', 'Confección'], color: 'purple', icon: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
            { name: 'Cerrajería', key: ['Cerrajeria', 'Cerrajería'], color: 'red', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' }
          ].map((cat, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-sm p-3 hover:shadow-md transition-shadow">
              <div className="flex flex-col items-center">
                <div className={`text-${cat.color}-600 bg-${cat.color}-100 p-2 rounded-full mb-2`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={cat.icon} />
                  </svg>
                </div>
                <h4 className="text-xs font-semibold text-gray-700 text-center mb-1">{cat.name}</h4>
                <p className="text-lg font-bold text-gray-800">
                  {loadingCategorias ? '...' : (cat.key.reduce((acc, k) => acc || conteosCategorias[k], null) || 0)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabla de Empresas del Mes Actual */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6">
          Empresas - {new Date(currentYear, currentMonth - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </h3>
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loadingEmpresas ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando empresas...</p>
            </div>
          ) : empresas.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No hay empresas con servicios este mes
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Empresa
                      </th>
                      <th 
                        className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('lead')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Lead Actual
                          {sortBy === 'lead' && (
                            <span className="text-blue-600">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('servicios')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Total Servicios
                          {sortBy === 'servicios' && (
                            <span className="text-blue-600">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('pagado')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Total Pagado
                          {sortBy === 'pagado' && (
                            <span className="text-blue-600">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getPaginatedEmpresas().map((empresa, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {empresa.companyName || 'Sin nombre'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                            {empresa.totalLeadsCost || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-orange-100 text-orange-800">
                            {empresa.serviceCount || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-semibold text-green-600">
                            {formatCurrency(empresa.amountDue)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, empresas.length)} de {empresas.length} empresas
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Anterior
                    </button>
                    <div className="flex items-center gap-2">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sección User Xervix */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6">User Xervix</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { name: 'User', key: ['user', 'User'], color: 'blue', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
            { name: 'User Patner', key: ['userPatner'], color: 'green', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
            { name: 'User Admin Unit', key: ['userAdminUnit', 'UserAdminUnit'], color: 'purple', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
            { name: 'Company Patner', key: ['companyPatner'], color: 'orange', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
            { name: 'Inmobiliarias', key: ['inmobiliaria', 'Inmobiliaria'], color: 'cyan', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
            { name: 'Total User', key: ['total'], color: 'indigo', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' }
          ].map((user, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-700">{user.name}</h3>
                <span className={`text-${user.color}-500 bg-${user.color}-100 p-2 rounded-full`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={user.icon} />
                  </svg>
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-800">
                {loadingRoles ? <span className="text-gray-400">...</span> : (user.key.reduce((acc, k) => acc || conteosRoles[k], null) || 0)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UsuariosEmpresas;
